import { ConfigManager } from './config.js';
export async function initializeConnectivityTesting() {
    const apiUrlInput = document.getElementById('api-url');
    const resultDiv = document.getElementById('connectivity-result');
    
    // Initialize API URL field with current base URL
    const baseUrl = await ConfigManager.getApiUrl('');
    apiUrlInput.value = baseUrl;
    logMessage(`Initialized API URL field with: ${baseUrl}`);
    
    // Test Connect Button
    document.getElementById('test-connect').addEventListener('click', async () => {
        logMessage('Test Connect clicked');
        await testEndpoint(apiUrlInput.value, 'test-connect');
    });
    
    // Test Inference Button
    document.getElementById('test-inference').addEventListener('click', async () => {
        logMessage('Test Inference clicked');
        await testEndpoint(apiUrlInput.value, 'test-inference');
    });

    // Test Upload Button
    document.getElementById('test-upload').addEventListener('click', async () => {
        logMessage('Test Upload clicked');
        await testUpload(apiUrlInput.value);
    });
    
    // Test Raw URL Button
    document.getElementById('test-raw').addEventListener('click', async () => {
        logMessage('Test Raw URL clicked');
        await testEndpoint(apiUrlInput.value);
    });

    document.getElementById('test-config').addEventListener('click', async () => {
        logMessage('Test API Config clicked');
        await testConfigResolution();
    });
}

async function testEndpoint(baseUrl, endpoint = '') {
    const resultDiv = document.getElementById('connectivity-result');
    resultDiv.style.display = 'block';
    resultDiv.className = 'connectivity-result';
    resultDiv.innerHTML = 'Testing connection...';
    
    const startTime = new Date().getTime();
    
    try {
        const url = endpoint ? `${baseUrl}/${endpoint}` : baseUrl;
        logMessage(`Testing endpoint: ${url}`);
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });
        
        const endTime = new Date().getTime();
        const duration = endTime - startTime;
        
        logMessage(`Response received in ${duration}ms with status: ${response.status}`);

        if (response.ok) {
            const data = await response.json();
            resultDiv.className = 'connectivity-result success';
            resultDiv.innerHTML = `
                <div>Status: ${response.status} (${response.statusText})</div>
                <div>Response Time: ${duration}ms</div>
                <div>Response: ${JSON.stringify(data, null, 2)}</div>
            `;
        } else {
            resultDiv.className = 'connectivity-result error';
            resultDiv.innerHTML = `
                <div>Status: ${response.status} (${response.statusText})</div>
                <div>Response Time: ${duration}ms</div>
                <div>Error: Failed to connect</div>
            `;
        }
    } catch (error) {
        const endTime = new Date().getTime();
        const duration = endTime - startTime;
        
        logMessage(`Error testing endpoint: ${error.message}`);
        
        resultDiv.className = 'connectivity-result error';
        resultDiv.innerHTML = `
            <div>Status: Error</div>
            <div>Response Time: ${duration}ms</div>
            <div>Error: ${error.message}</div>
        `;
    }
}

async function testUpload(baseUrl) {
    const resultDiv = document.getElementById('connectivity-result');
    resultDiv.style.display = 'block';
    resultDiv.className = 'connectivity-result';
    resultDiv.innerHTML = 'Preparing data for upload test...';
    
    const startTime = new Date().getTime();
    logMessage("Starting test upload...");
    
    try {
        await Excel.run(async (context) => {
            // Get workbook name
            const workbook = context.workbook;
            workbook.load("name");
            await context.sync();
            logMessage(`Loaded workbook: ${workbook.name}`);
            
            // Find Census sheet
            logMessage("Finding Census sheet...");
            const sheets = context.workbook.worksheets;
            sheets.load("items/name");
            await context.sync();

            const sheet = sheets.items.find(s => s.name === 'Census');
            if (!sheet) {
                throw new Error("Census sheet not found - please ensure you have a sheet named 'Census'");
            }

            const range = sheet.getUsedRange();
            range.load(["values", "rowCount", "columnCount"]);
            await context.sync();

            if (!range.values || range.values.length === 0) {
                logMessage("Error: Census sheet is empty");
                throw new Error(`Census sheet is empty`);
            }

            logMessage(`Found Census sheet with ${range.rowCount} rows and ${range.columnCount} columns`);

            // Convert to JSON similar to processCensusData
            const headers = range.values[0];
            const data = [];
            let processedRows = 0;
            let skippedRows = 0;

            logMessage("Processing rows...");
            for (let i = 1; i < range.values.length; i++) {
                const row = {};
                row["_rowNumber"] = i + 1;
                
                let isEmptyRow = true;
                for (let j = 0; j < headers.length; j++) {
                    if (range.values[i][j] !== undefined && range.values[i][j] !== '') {
                        row[headers[j]] = range.values[i][j];
                        isEmptyRow = false;
                    }
                }
                if (!isEmptyRow) {
                    data.push(row);
                    processedRows++;
                } else {
                    skippedRows++;
                }
            }

            logMessage(`Processed ${processedRows} rows, skipped ${skippedRows} empty rows`);

            const testData = {
                filename: workbook.name,
                sheets: {
                    'Census': {
                        headers: headers,
                        data: data,
                        totalRows: range.rowCount - 1
                    }
                },
                products: [],  // Add empty products array
                censusType: "Eligible",  // Add default census type
                enable_ai: false,  // Add AI flag
                confidenceScore: 80,  // Add default confidence score
                columnMappings: {}  // Add empty column mappings
            };

            // Send test upload request
            const url = `${baseUrl}/test-upload`;
            logMessage(`Sending test upload to: ${url}`);
            logMessage(`Test data size: ${JSON.stringify(testData).length} bytes`);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify(testData)
            });

            const endTime = new Date().getTime();
            const duration = endTime - startTime;
            logMessage(`Response received in ${duration}ms with status: ${response.status}`);

            if (response.ok) {
                const data = await response.json();
                logMessage("Test upload successful");
                resultDiv.className = 'connectivity-result success';
                resultDiv.innerHTML = `
                    <div>Status: ${response.status} (${response.statusText})</div>
                    <div>Response Time: ${duration}ms</div>
                    <div>Data Size: ${testData.sheets.Census.data.length} rows</div>
                    <div>Response: ${JSON.stringify(data, null, 2)}</div>
                `;
            } else {
                logMessage(`Test upload failed with status: ${response.status}`);
                resultDiv.className = 'connectivity-result error';
                resultDiv.innerHTML = `
                    <div>Status: ${response.status} (${response.statusText})</div>
                    <div>Response Time: ${duration}ms</div>
                    <div>Error: Upload test failed</div>
                `;
            }
        });
    } catch (error) {
        const endTime = new Date().getTime();
        const duration = endTime - startTime;
        
        logMessage(`Test upload error: ${error.message}`);
        resultDiv.className = 'connectivity-result error';
        resultDiv.innerHTML = `
            <div>Status: Error</div>
            <div>Response Time: ${duration}ms</div>
            <div>Error: ${error.message}</div>
        `;
    }
}

function logMessage(message) {
    console.log(message);
    const logDisplay = document.getElementById("log-display");
    const loggingEnabled = document.getElementById("enable-logging")?.checked;
    
    if (logDisplay && loggingEnabled) {
        const timestamp = new Date().toLocaleTimeString();
        logDisplay.style.display = "block";
        const newMessage = document.createElement('div');
        newMessage.textContent = `${timestamp}: ${message}`;
        logDisplay.appendChild(newMessage);
        logDisplay.scrollTop = logDisplay.scrollHeight;
    } else if (logDisplay) {
        logDisplay.style.display = "none";
    }
}





